import React, { useEffect, useRef, useState } from 'react';
import { Modal, UnstyledButton, Loader } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faXmark, faCheck } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { notifications } from '@mantine/notifications';
import type { ReportView } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import { printersService } from '../../../../services/printers/printers.service';
import type { PrinterView } from '../../../../services/printers/printers.service';
import { captureReceiptSnapshot } from '../receipt/shared/receipt.utils';
import { PosReportView } from './PosReportView';
import { resolveReceiptAutoPrinterId, resolveReceiptPrintTarget } from '../../hooks/fixed-printer.util';
import classes from './pos-report-modal.module.scss';

const REPORT_PRINTER_STORAGE_KEY = 'posReportPrinterId';

export type PosReportModalProps = {
  opened: boolean;
  onClose: () => void;
  report: ReportView | null;
  loading: boolean;
  title: string;
  tenantId: string;
  employeeSession: string;
};

// Summary: Full report modal — renders a ReportView, prints it via the order-less print-report path.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-reports
const PosReportModalComponent: React.FC<PosReportModalProps> = ({
  opened,
  onClose,
  report,
  loading,
  title,
  tenantId,
  employeeSession,
}) => {
  const captureRef = useRef<HTMLDivElement>(null);
  const printInFlightRef = useRef(false);
  const [printers, setPrinters] = useState<PrinterView[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedPrinterId, setSelectedPrinterId] = useState<string | null>(() => localStorage.getItem(REPORT_PRINTER_STORAGE_KEY));
  const [pickCandidates, setPickCandidates] = useState<PrinterView[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!opened || !employeeSession) {
      return;
    }
    printersService
      .listActive(tenantId, employeeSession)
      .then((data) => {
        setPrinters(data);
        // Billable printer (or the 0-billable device fallback) wins; else legacy key, else first.
        const autoId = resolveReceiptAutoPrinterId(data);
        const legacy = localStorage.getItem(REPORT_PRINTER_STORAGE_KEY);
        if (autoId != null) {
          setSelectedPrinterId(String(autoId));
        } else if (legacy && data.some((p) => String(p.id) === legacy)) {
          setSelectedPrinterId(legacy);
        } else if (data.length > 0) {
          setSelectedPrinterId(String(data[0].id));
        }
      })
      .catch(() => undefined);
  }, [opened, tenantId, employeeSession]);

  // Billable/sole auto-prints; 2+ billable or 2+ active open the picker (billable-filtered if any).
  const handlePrintClick = () => {
    const target = resolveReceiptPrintTarget(printers);
    if (target.kind === 'print') {
      setSelectedPrinterId(String(target.printerId));
      void handlePrint(String(target.printerId));
    } else {
      setPickCandidates(target.kind === 'pick' && target.candidates ? target.candidates : printers);
      setPickerOpen(true);
    }
  };

  const handlePrint = async (printerIdOverride?: string) => {
    const printerId = printerIdOverride ?? selectedPrinterId;
    if (printInFlightRef.current || !printerId || !captureRef.current) {
      return;
    }
    try {
      printInFlightRef.current = true;
      setIsPrinting(true);
      const image = await captureReceiptSnapshot(captureRef.current);
      if (!image) {
        notifications.show({ color: 'red', title: 'שגיאה', message: 'לא ניתן ליצור תמונה להדפסה', autoClose: 3000 });
        return;
      }
      await printersService.printReport(tenantId, Number(printerId), image, employeeSession);
      localStorage.setItem(REPORT_PRINTER_STORAGE_KEY, printerId);
      notifications.show({ color: 'green', title: 'הדפסה', message: 'הדוח נשלח להדפסה', autoClose: 3000 });
      setPickerOpen(false);
    } catch (error: unknown) {
      notifications.show({
        color: 'red',
        title: 'ההדפסה נכשלה',
        message: error instanceof Error ? error.message : 'שגיאה לא ידועה',
        autoClose: 3000,
      });
    } finally {
      printInFlightRef.current = false;
      setIsPrinting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      padding={0}
      radius="md"
      centered
      size="md"
      zIndex={400}
      classNames={{ content: classes.modalContent, body: classes.modalBody }}
    >
      <div className={classes.shell} dir="rtl">
        <div className={classes.topBar}>
          <span className={classes.topTitle}>{title}</span>
          <div className={classes.topActions}>
            <UnstyledButton
              component="button"
              type="button"
              data-testid="report-print"
              className={classes.iconButton}
              onClick={handlePrintClick}
              disabled={!report || loading}
              aria-label="הדפס"
            >
              <FontAwesomeIcon icon={faPrint as IconProp} />
            </UnstyledButton>
            <UnstyledButton component="button" type="button" data-testid="report-close" className={classes.iconButton} onClick={onClose} aria-label="סגור">
              <FontAwesomeIcon icon={faXmark as IconProp} />
            </UnstyledButton>
          </div>
        </div>

        <div className={classes.body}>
          {loading ? (
            <div className={classes.center}><Loader /></div>
          ) : report ? (
            <PosReportView report={report} variant="screen" />
          ) : (
            <div className={classes.center}>לא ניתן להפיק דוח</div>
          )}
        </div>
      </div>

      {report && (
        <div className={classes.offscreen} aria-hidden="true">
          <PosReportView report={report} variant="print" innerRef={captureRef} />
        </div>
      )}

      <Modal
        opened={pickerOpen}
        onClose={() => { if (!isPrinting) setPickerOpen(false); }}
        title={<strong className={classes.pickerModalTitle}>בחר מדפסת</strong>}
        centered
        radius="md"
        dir="rtl"
        zIndex={500}
        closeButtonProps={{ 'data-testid': 'report-printer-picker-close' } as Record<string, string>}
        classNames={{ header: classes.pickerModalHeader, title: classes.pickerModalTitle }}
      >
        <div className={classes.pickerStack}>
          {pickCandidates.length === 0 ? (
            <span className={classes.pickerEmpty}>אין מדפסות פעילות</span>
          ) : (
            <div className={classes.pickerList}>
              {pickCandidates.map((printer) => {
                return (
                  <UnstyledButton
                    key={printer.id}
                    component="button"
                    type="button"
                    data-testid={`report-printer-option-${printer.id}`}
                    className={classes.pickerItem}
                    onClick={() => { void handlePrint(String(printer.id)); }}
                    disabled={isPrinting}
                  >
                    <span>{printer.name}</span>
                  </UnstyledButton>
                );
              })}
            </div>
          )}
          <div className={classes.pickerActions}>
            <Button variant="cancel" fullWidth onClick={() => setPickerOpen(false)} disabled={isPrinting} dataTestId="report-printer-cancel">
              ביטול
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export const PosReportModal = React.memo(PosReportModalComponent);
